import { ASSET_STATE_COUNTS_QUERY_KEY, useGetAssetStateCounts } from '@/hooks/api/assets/useGetAssetStateCounts';
import { LIST_ASSETS_BY_STATE_QUERY_KEY, useFetchAssetsByState } from '@/hooks/api/assets/useFetchAssetsByState';
import { AssetStateChart } from '@/components/AssetStateChart/AssetStateChart';
import { SearchTable } from '@/components/Search/SearchTable';
import { usePreserveDataDuringLoading } from '@/components/FilterData/hooks/usePreserveDataDuringLoading';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Box, styled } from '@mui/material';
import { useState, useEffect } from 'react';
import { AssetState } from '@/api/generatedApi';
import { AssetStateChartSkeleton } from '@/components/AssetStateChart/AssetStateChartSkeleton';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { getApiError } from '@/types/apiError';
import { useQueryClient } from '@tanstack/react-query';
import { isValidAssetState } from '@/utils/assetStateDisplayText';

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: '400px',
  height: '100%',
  display: 'flex',
  flexDirection: 'row',
});

const ChartContainer = styled(Box)({
  width: '275px',
  flexShrink: 0,
  marginBottom: '16px',
});

const TableBox = styled(Box)({
  flex: 1,
  minWidth: '400px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const StatusPage = () => {
  usePageTitle('Status');
  const { data: assetStateCounts, isLoading, isRefetching, error } = useGetAssetStateCounts({});
  const [selectedAssetState, setSelectedAssetState] = useState<AssetState>(AssetState.Finished);
  const { fetchData, error: assetsError } = useFetchAssetsByState({
    assetState: selectedAssetState,
  });
  const queryClient = useQueryClient();
  const preservedAssetStateCounts = usePreserveDataDuringLoading(assetStateCounts, isLoading || isRefetching);

  const assetsTotal = Object.entries(preservedAssetStateCounts || {})
    .filter(([key]) => isValidAssetState(key))
    .reduce((sum, [, count]) => sum + count, 0);

  useEffect(() => {
    if (
      selectedAssetState &&
      preservedAssetStateCounts?.[selectedAssetState as keyof typeof preservedAssetStateCounts] === 0
    ) {
      const firstStateWithCount = Object.entries(preservedAssetStateCounts || {})
        .filter(([key]) => isValidAssetState(key))
        .find(([_, count]) => count > 0);
      if (firstStateWithCount) {
        setSelectedAssetState(firstStateWithCount[0] as AssetState);
      }
    }
  }, [preservedAssetStateCounts, selectedAssetState]);

  const handleStateClick = (state: AssetState) => {
    setSelectedAssetState(state);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: [ASSET_STATE_COUNTS_QUERY_KEY], exact: false });
    queryClient.invalidateQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
  };

  if (error) {
    return <ApiErrorAlert error={getApiError(error)} />;
  }

  return (
    <ContentArea>
      <Box sx={{ width: '275px', flexShrink: 0 }}>
        {preservedAssetStateCounts ? (
          <ChartContainer>
            <AssetStateChart
              onRefresh={handleRefresh}
              selectedAssetState={selectedAssetState}
              data={preservedAssetStateCounts}
              assetsTotal={assetsTotal}
              onStateClick={handleStateClick}
            />
          </ChartContainer>
        ) : (
          <AssetStateChartSkeleton onRefresh={handleRefresh} />
        )}
      </Box>
      <TableBox>
        <Box flex={1} minHeight={0} padding='6px 6px 6px 0'>
          <SearchTable
            fetchMatches={fetchData}
            isLoading={isLoading || isRefetching}
            error={assetsError}
            shouldShowSearchTabs={false}
            bulkSelectionKey={selectedAssetState}
          />
        </Box>
      </TableBox>
    </ContentArea>
  );
};
